// src/context/AuthContext.js
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { subscribeToAuthChanges } from "../services/authService";
import {
  createUserProfile,
  getUserProfile,
  unlockJob as firestoreUnlockJob,
  getUnlockedJobIds,
  getSavedJobIds,
  toggleSaveJob,
} from "../services/firestoreService";

const AuthContext = createContext(null);

const ADMIN_EMAILS = [
  "x89designs@gmail.com",
];

export function AuthProvider({ children }) {
  const [currentUser,    setCurrentUser]    = useState(null);
  const [userProfile,    setUserProfile]    = useState(null);
  const [userRole,       setUserRole]       = useState(null);
  const [credits,        setCredits]        = useState(0);
  const [unlockedJobIds, setUnlockedJobIds] = useState(new Set());
  const [savedJobIds,    setSavedJobIds]    = useState(new Set());
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (user) => {
      setCurrentUser(user);

      if (user) {
        const isAdminEmail = ADMIN_EMAILS.includes(user.email);

        try {
          const profile = await createUserProfile(user, null);
          const role    = isAdminEmail ? "admin" : (profile.role || "freelancer");
          setUserRole(role);
          setUserProfile({ ...profile, role });
          setCredits(profile.credits ?? 0);

          // Load unlock history in background — don't block auth
          getUnlockedJobIds(user.uid)
            .then(ids => setUnlockedJobIds(ids))
            .catch(() => {});
          getSavedJobIds(user.uid)
            .then(ids => setSavedJobIds(ids))
            .catch(() => {});
        } catch (err) {
          console.error("Firestore profile error:", err);
          const role = isAdminEmail ? "admin" : "freelancer";
          setUserRole(role);
          setUserProfile({
            uid:         user.uid,
            displayName: user.displayName || "",
            email:       user.email,
            role,
            credits:     0,
            skills:      [],
          });
        }
      } else {
        setUserRole(null);
        setUserProfile(null);
        setCredits(0);
        setUnlockedJobIds(new Set());
        setSavedJobIds(new Set());
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!currentUser) return;
    const profile = await getUserProfile(currentUser.uid);
    if (profile) {
      const role = ADMIN_EMAILS.includes(currentUser.email)
        ? "admin"
        : (profile.role || "freelancer");
      setUserProfile({ ...profile, role });
      setUserRole(role);
      setCredits(profile.credits ?? 0);
    }
  }, [currentUser]);

  const unlockJob = useCallback(async (job) => {
    if (!currentUser) throw new Error("Not authenticated");
    const result = await firestoreUnlockJob(currentUser.uid, job);
    if (!result.alreadyUnlocked) {
      setCredits(prev => prev - 2);
      setUnlockedJobIds(prev => new Set([...prev, job.id]));
    }
    return result;
  }, [currentUser]);

  const isJobUnlocked = useCallback((jobId) => {
    return unlockedJobIds.has(jobId);
  }, [unlockedJobIds]);

  const toggleSave = useCallback(async (jobId) => {
    if (!currentUser) return;
    const nowSaved = await toggleSaveJob(currentUser.uid, jobId);
    setSavedJobIds(prev => {
      const next = new Set(prev);
      if (nowSaved) next.add(jobId); else next.delete(jobId);
      return next;
    });
    return nowSaved;
  }, [currentUser]);

  const isJobSaved = useCallback((jobId) => savedJobIds.has(jobId), [savedJobIds]);

  return (
    <AuthContext.Provider value={{
      currentUser, userProfile, userRole, loading,
      credits, unlockJob, isJobUnlocked,
      savedJobIds, toggleSave, isJobSaved,
      refreshProfile,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
